import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/cn'

const inputVariants = cva(
  'w-full rounded-lg bg-white/5 border border-white/10 text-[#EEEEEE] placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#5E6AD2]/40 focus:border-transparent transition-colors text-base md:text-sm disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      hasError: {
        true: 'border-red-500/40 focus:ring-red-500/40',
        false: '',
      },
    },
    defaultVariants: {
      hasError: false,
    },
  }
)

export interface FormInputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  label?: ReactNode
  error?: string
  containerClassName?: string
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ label, error, className, containerClassName, hasError, id, ...props }, ref) => {
    const showError = Boolean(error) || hasError

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <label
            htmlFor={id}
            className="block text-xs font-medium text-[#888888] uppercase tracking-wider"
          >
            {label}
          </label>
        )}
        <input
          id={id}
          ref={ref}
          className={cn(inputVariants({ hasError: showError }), className)}
          {...props}
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    )
  }
)

FormInput.displayName = 'FormInput'
